import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { CollectionModel, CollectionReport } from 'model/collection/CollectionModels';
import { calculateAmountSummary, getNextEmailDate } from 'utils/Common';
import dayjs from 'dayjs';
import HalfBorderedGrid from 'components/common/HalfBorderedGrid';

export class ReportProps {
  report: CollectionReport;
  upcomingCollection?: CollectionModel;
}

const StatBox = ({ label, value }: { label: string, value: any }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom>{label}</Typography>
    <Typography variant="h4">{value}</Typography>
  </Box>
);

const Analytics: FunctionComponent<ReportProps> = ({ report, upcomingCollection }) => {
  const totals = calculateAmountSummary(report.collectionTotals ?? []);
  const hasActivity = !!report.notificationLastSent;
  const lastSent = hasActivity ? dayjs(report.notificationLastSent).format("Do MMM") : null;

  const nextOn = upcomingCollection
    ? getNextEmailDate(upcomingCollection.schedule, upcomingCollection.dayOfTheMonth ?? null, upcomingCollection.dayOfTheWeek ?? null).format("Do MMM")
    : hasActivity ? dayjs(report.notificationNext).format("Do MMM") : null;

  const activityItems = hasActivity ? [
    <StatBox label="Last sent on" value={<Typography variant="h5">{lastSent}</Typography>}/>,
    <StatBox label="Next on" value={<Typography variant="h5">{nextOn}</Typography>}/>,
  ] : [];

  const statsItems = [
    <Box>
      <Link to="/notifications">
        <Typography variant="subtitle1" gutterBottom>Sent Total</Typography>
        <Typography variant="h4">{report.notificationsTotal}</Typography>
      </Link>
    </Box>,
    <StatBox label="YTD" value={report.notificationsYearToDate}/>,
    <StatBox label="Total" value={`$${totals}`}/>,
    <StatBox label="Active Collections" value={report.activeCollectionCount}/>,
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h5" gutterBottom>Summary</Typography>
        {hasActivity ? (
          <Typography className="wizard-subtitle-color" variant="body1" gutterBottom sx={{ wordBreak: "break-word" }} marginBottom={1}>
            {"Since the last notification that was sent on "}
            <Chip label={lastSent} component="span" variant="outlined" className="chip"/>
            {", the total amount collected should be "}
            <Chip label={`$${totals}`} variant="outlined" component="span" className="chip"/>
          </Typography>
        ) : (
          <Typography className="wizard-subtitle-color" variant="body1" gutterBottom sx={{ wordBreak: "break-word" }} marginBottom={1}>
            {"The total amount collected is "}
            <Chip label={`$${totals}`} variant="outlined" component="span" className="chip"/>
          </Typography>
        )}
        <HalfBorderedGrid sx={{ mt: 2 }} itemsPerRow={hasActivity ? 3 : 2} children={[...activityItems, ...statsItems]}/>
      </CardContent>
    </Card>
  );
}

export default Analytics;
